import {
  createContext,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

type BackToTopValue = {
  scrollToTop(): void;
  ref: RefObject<HTMLDivElement | typeof window>;
};

const BackToTopContext = createContext<Partial<BackToTopValue>>({});

const BackToTopProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const ref = useRef<HTMLDivElement | typeof window>(window);

  const scrollToTop = useCallback(
    () =>
      ref.current.scrollTo({
        top: 0,
        behavior: "smooth",
      }),
    []
  );

  const value = useMemo(
    () => ({
      ref,
      scrollToTop,
    }),
    [scrollToTop]
  );

  return (
    <BackToTopContext.Provider value={value}>
      {children}
    </BackToTopContext.Provider>
  );
};

export default BackToTopProvider;

export const useBackToTop = (): BackToTopValue => {
  const { ref, scrollToTop } = useContext(BackToTopContext);
  if (!ref || !scrollToTop) {
    throw Error("Back to top context not mounted");
  }
  return { ref, scrollToTop };
};
